import { BaseService } from './BaseService.js';

/**
 * Invoice Service
 * Handles all invoice operations using Laravel API
 */
export class InvoiceService extends BaseService {
  constructor() {
    super('invoices'); // Laravel endpoint: /api/invoices
  }

  /**
   * Get invoices by status
   * @param {string} status - Invoice status
   * @returns {Promise<Array>} Filtered invoices
   */
  async getByStatus(status) {
    return this.list(1, 50, { status });
  }

  /**
   * Get customer invoices
   * @param {string} customerId - Customer ID
   * @returns {Promise<Array>} Filtered invoices
   */
  async getCustomerInvoices(customerId) {
    return this.list(1, 50, { customer_id: customerId });
  }

  /**
   * Generate invoice from booking
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object>} Generated invoice
   */
  async generateFromBooking(bookingId) {
    return this.customCall('generate-from-booking', 'POST', { booking_id: bookingId });
  }

  /**
   * Mark invoice as paid
   * @param {string} invoiceId - Invoice ID
   * @param {Object} paymentData - Payment data
   * @returns {Promise<Object>} Updated invoice
   */
  async markAsPaid(invoiceId, paymentData = {}) {
    return this.update(invoiceId, { 
      status: 'bezahlt',
      paid_at: new Date().toISOString(),
      ...paymentData
    });
  }

  /**
   * Send invoice by email
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise<boolean>} Success status
   */
  async sendByEmail(invoiceId) {
    return this.customCall(`${invoiceId}/send`, 'POST');
  }

  /**
   * Download invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise<string>} PDF URL
   */
  async downloadPDF(invoiceId) {
    return this.customCall(`${invoiceId}/pdf`);
  }

  /**
   * Get invoice statistics
   * @returns {Promise<Object>} Invoice statistics
   */
  async getStatistics() {
    return this.customCall('statistics');
  }

  /**
   * Get overdue invoices
   * @returns {Promise<Array>} Overdue invoices
   */
  async getOverdue() {
    return this.customCall('overdue');
  }
}

// Export singleton instance for backward compatibility
const invoiceService = new InvoiceService();
export default invoiceService; 